import React from 'react';
import { Text, Heading } from '@/components/ui/design-system';

export const RefundPolicyContent = () => (
  <>
    <Heading level={2} className="heading-underline-animated pb-2 block">Introduction</Heading>
    <Text size="lg" className="mt-4 mb-5">
      At PAVISHA, we want you to be completely satisfied with your purchase. This Refund Policy 
      outlines our procedures for returns, refunds, and exchanges. By making a purchase from us, 
      you agree to the terms outlined in this policy.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Returns and Refunds</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We accept returns of products under the following conditions:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>The return request is made within 7 days of delivery</li>
      <li>The product is in its original condition, unused, and with all original packaging</li>
      <li>The product is not damaged or tampered with</li>
      <li>You provide proof of purchase (order number, invoice, etc.)</li>
    </ul>
    <Text size="lg" className="mt-4 mb-5">
      Returns that meet these conditions will be eligible for a full refund of the purchase price, 
      excluding shipping charges.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Non-Returnable Items</Heading>
    <Text size="lg" className="mt-4 mb-5">
      The following items cannot be returned:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>Custom or personalized products</li>
      <li>Products that have been used or show signs of use</li>
      <li>Products with damaged packaging or missing parts</li>
      <li>Products specifically marked as non-returnable at the time of purchase</li>
      <li>Wholesale or bulk orders (special terms apply; please contact us directly)</li>
    </ul>

    <Heading level={2} className="heading-underline-animated pb-2 block">Return Process</Heading>
    <Text size="lg" className="mt-4 mb-5">
      To initiate a return, please follow these steps:
    </Text>
    <ol className="list-decimal list-inside space-y-2 mt-4 mb-5">
      <li>Contact our customer service team at <a href="mailto:returns@pavisha.com">returns@pavisha.com</a> or call <a href="tel:+917979912640">+91 79799 12640</a> within 7 days of receiving your order</li>
      <li>Provide your order number, the items you wish to return, and the reason for the return</li>
      <li>Our team will review your request and provide you with a Return Authorization Number (RAN)</li>
      <li>Package the items in their original packaging with the RAN clearly visible</li>
      <li>Ship the items to the return address provided by our customer service team</li>
    </ol>
    <Text size="lg" className="mt-4 mb-5">
      Please note that you are responsible for the return shipping costs unless the return is due to 
      our error (wrong item shipped, defective product, etc.).
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Refund Processing</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Once we receive and inspect the returned products, we will process your refund according 
      to the following timeline:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>Inspection: 1-3 business days after receipt of the returned items</li>
      <li>Refund approval: 1-2 business days after inspection</li>
      <li>Refund processing: 5-7 business days (depending on your payment method and bank)</li>
    </ul>
    <Text size="lg" className="mt-4 mb-5">
      Refunds will be issued to the original payment method used for the purchase. For credit/debit 
      card purchases, please allow 5-7 business days for the refund to appear on your statement. For 
      other payment methods, processing times may vary.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Exchanges</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We offer exchanges for products of equal value if you prefer to receive a replacement instead 
      of a refund. To request an exchange:
    </Text>
    <ol className="list-decimal list-inside space-y-2 mt-4 mb-5">
      <li>Follow the return process outlined above</li>
      <li>Specify that you would like an exchange instead of a refund</li>
      <li>Provide details of the product you would like to receive as a replacement</li>
    </ol>
    <Text size="lg" className="mt-4 mb-5">
      If the replacement product has a different price than the original purchase:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>If the replacement costs more, you will need to pay the difference</li>
      <li>If the replacement costs less, we will refund the difference</li>
    </ul>

    <Heading level={2} className="heading-underline-animated pb-2 block">Damaged or Defective Products</Heading>
    <Text size="lg" className="mt-4 mb-5">
      If you receive a damaged or defective product, please contact us within 48 hours of 
      delivery. Please provide:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>Your order number</li>
      <li>A description of the damage or defect</li>
      <li>Photos of the damaged product and packaging</li>
    </ul>
    <Text size="lg" className="mt-4 mb-5">
      For verified cases of damaged or defective products, we will provide a full refund including 
      shipping costs or send a replacement at no additional cost to you.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Cancellations</Heading>
    <Text size="lg" className="mt-4 mb-5">
      You may cancel an order before it ships by contacting our customer service team. Once an order 
      has been shipped, it cannot be canceled, and our standard return policy will apply.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      For orders that are canceled before shipping, you will receive a full refund including any 
      shipping charges.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Refund Policy for Custom Orders</Heading>
    <Text size="lg" className="mt-4 mb-5">
      For custom or made-to-order products:
    </Text>
    <ul className="list-disc list-inside space-y-2 mt-4 mb-5">
      <li>Orders cannot be canceled once production has begun</li>
      <li>Returns are not accepted unless the product is defective or significantly different from the specifications</li>
      <li>If a custom product is defective, we will repair, replace, or refund at our discretion</li>
    </ul>

    <Heading level={2} className="heading-underline-animated pb-2 block">Changes to This Refund Policy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We reserve the right to modify this Refund Policy at any time. Changes will take effect 
      immediately upon posting on our website. The policy in effect at the time of purchase will 
      apply to any returns or refunds for that purchase.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      The date at the top of this page indicates when this Refund Policy was last updated.
    </Text>
  </>
);
